import React, { useState, useRef } from 'react';
import { Send, Image, Smile, Mic, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

/**
 * Message Input Component
 * Mobile-optimized with larger touch targets and better UX
 */
const MessageInput = ({
  messageInput,
  setMessageInput,
  handleSendMessage,
  isUploading,
  setShowGifPicker,
  setShowEmojiPicker,
  handleMediaUpload,
  disabled = false,
  placeholder = "Type a message...",
  maxLength = 2000,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (messageInput.trim() && !disabled) {
      handleSendMessage(e);
    }
  };

  const handleKeyDown = (e) => {
    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file && handleMediaUpload) {
      await handleMediaUpload(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <form 
      onSubmit={handleSubmit}
      className="border-t bg-white dark:bg-gray-900 p-2 md:p-4"
    >
      <div className={`
        flex items-end gap-2 
        rounded-2xl border-2 transition-colors duration-200
        ${isFocused ? 'border-blue-500 shadow-sm' : 'border-gray-200 dark:border-gray-700'}
        bg-gray-50 dark:bg-gray-800
        p-2
      `}>
        {/* Media/GIF/Emoji buttons */}
        <div className="flex items-center gap-1">
          {/* Image upload button - Larger touch target for mobile */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10 md:h-9 md:w-9 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 touch-manipulation"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading || disabled}
            title="Upload image"
          >
            {isUploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-gray-500" />
            ) : (
              <Image className="h-5 w-5 text-gray-500" />
            )}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={handleFileSelect}
          />

          {/* GIF button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10 md:h-9 md:w-9 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 touch-manipulation"
            onClick={() => setShowGifPicker && setShowGifPicker(true)}
            disabled={disabled}
            title="Send GIF"
          >
            <span className="text-sm font-bold text-gray-500">GIF</span>
          </Button>

          {/* Emoji button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-10 w-10 md:h-9 md:w-9 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 touch-manipulation"
            onClick={() => setShowEmojiPicker && setShowEmojiPicker(true)}
            disabled={disabled}
            title="Add emoji"
          >
            <Smile className="h-5 w-5 text-gray-500" />
          </Button>
        </div>

        {/* Text input */}
        <div className="flex-1 relative">
          <Textarea
            ref={textareaRef}
            value={messageInput}
            onChange={(e) => setMessageInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            className="
              min-h-[44px] max-h-32 resize-none border-0 bg-transparent
              focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0
              py-3 px-2 text-base
              placeholder:text-gray-400
            "
            rows={1}
          />
          {/* Character count - only show when near limit */}
          {messageInput.length > maxLength * 0.8 && (
            <span className={`
              absolute bottom-1 right-2 text-xs
              ${messageInput.length >= maxLength ? 'text-red-500' : 'text-gray-400'}
            `}>
              {messageInput.length}/{maxLength}
            </span>
          )}
        </div>

        {/* Send button - Large touch target */}
        <Button
          type="submit"
          size="icon"
          disabled={!messageInput.trim() || disabled}
          className={`
            h-11 w-11 md:h-10 md:w-10 rounded-full shrink-0 touch-manipulation
            transition-all duration-200
            ${messageInput.trim() && !disabled
              ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400'
            }
          `}
        >
          <Send className="h-5 w-5" />
        </Button>
      </div>

      {/* Mobile hint */}
      <p className="text-xs text-gray-400 mt-1 text-center md:hidden">
        Press Enter to send • Shift+Enter for new line
      </p>
    </form>
  );
};

export default MessageInput;
